import { c as _c } from "react/compiler-runtime";
import { type DefaultNodeTypes, type SerializedLinkNode } from '@payloadcms/richtext-lexical';
import { type SerializedEditorState } from '@payloadcms/richtext-lexical/lexical';
import { type JSXConvertersFunction, RichText as RichTextWithoutBlocks } from '@payloadcms/richtext-lexical/react';
import { UploadJSXConverter } from '@/components/RichText/converters/CustomUploadConverter';
import { HeadingConverter } from '@/components/RichText/converters/HeadingConverter';
import { LinkJSXConverter } from '@/components/RichText/converters/LinkJSXConverter';
import { ParagraphConverter } from '@/components/RichText/converters/ParagraphConverter';
import { METRICS_PATH, STATISTICS_PATH } from '@/constants';
import { cn } from '@tallinn-arvudes/ui/utils';
import { type HTMLAttributes } from 'react';

// Add custom block components types here
// type NodeTypes = DefaultNodeTypes | SerializedBlockNode<SomeBlockProps>;
type NodeTypes = DefaultNodeTypes;
const internalDocToHref = ({
  linkNode
}: {
  linkNode: SerializedLinkNode;
}) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const {
    value,
    relationTo
  } = linkNode.fields.doc! as any;
  if (typeof value !== 'object') {
    throw new Error('Expected value to be an object');
  }
  if (value.breadcrumbs?.length) {
    return value.breadcrumbs[value.breadcrumbs.length - 1].url;
  }
  const slug = value.slug;
  if (relationTo === 'metrics') {
    return `${METRICS_PATH}/${value.id}`;
  }
  if (relationTo === 'statistics') {
    return `${STATISTICS_PATH}/${value.id}`;
  }
  return `/${slug}`;
};
type JSXConvertersArgs = Parameters<JSXConvertersFunction<NodeTypes>>[0];
type JSXConvertersReturn = ReturnType<JSXConvertersFunction<NodeTypes>>;
const jsxConverters = ({
  defaultConverters,
  lightLinks
}: JSXConvertersArgs & {
  lightLinks?: boolean;
}): JSXConvertersReturn => ({
  ...defaultConverters,
  ...LinkJSXConverter({
    internalDocToHref,
    light: lightLinks
  }),
  ...UploadJSXConverter,
  ...HeadingConverter,
  ...ParagraphConverter
});
type Props = {
  data: SerializedEditorState;
  enableGutter?: boolean;
  enableProse?: boolean;
  lightLinks?: boolean;
} & HTMLAttributes<HTMLDivElement>;
export default function RichText(props) {
  const $ = _c(15);
  let className;
  let lightLinks;
  let rest;
  let t0;
  if ($[0] !== props) {
    ({
      className,
      enableGutter: t0,
      lightLinks,
      ...rest
    } = props);
    $[0] = props;
    $[1] = className;
    $[2] = lightLinks;
    $[3] = rest;
    $[4] = t0;
  } else {
    className = $[1];
    lightLinks = $[2];
    rest = $[3];
    t0 = $[4];
  }
  const enableGutter = t0 === undefined ? false : t0;
  let t1;
  if ($[5] !== lightLinks) {
    t1 = t2 => {
      const {
        defaultConverters
      } = t2;
      return jsxConverters({
        defaultConverters,
        lightLinks
      });
    };
    $[5] = lightLinks;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const t2 = !enableGutter;
  let t3;
  if ($[7] !== className || $[8] !== enableGutter || $[9] !== t2) {
    t3 = cn({
      container: enableGutter,
      "max-w-none": t2
    }, className);
    $[7] = className;
    $[8] = enableGutter;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== rest || $[12] !== t1 || $[13] !== t3) {
    t4 = <RichTextWithoutBlocks converters={t1} className={t3} {...rest} />;
    $[11] = rest;
    $[12] = t1;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
}